import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';
import { API_ADMIN_END_POINT } from '../../Utils/Constant';
import { toast } from 'react-toastify';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

const Users = ({ users, refreshUsers }) => {
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [localUsers, setLocalUsers] = useState(users || []);
  const [deletingId, setDeletingId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Update local users when prop changes
  useEffect(() => {
    if (users) {
      setLocalUsers(users);
      setCurrentPage(1); // Reset to first page when users change
    }
  }, [users]);

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = localUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(localUsers.length / usersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      // If refreshUsers prop is provided, use it
      if (refreshUsers) {
        await refreshUsers();
      } 
      // Otherwise, fetch users directly
      else {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_ADMIN_END_POINT}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data) {
          setLocalUsers(response.data);
        }
      }
      
      toast.success('User list refreshed successfully');
    } catch (error) {
      console.error('Error refreshing users:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh users');
    } finally {
      setRefreshing(false);
    }
  };

  // Check if there's at least one admin
  const hasAdmin = localUsers.some(user => user.role === 'admin');
  // Count how many admins we have
  const adminCount = localUsers.filter(user => user.role === 'admin').length;

  const handleChangeRole = async (userId, newRole = 'admin') => {
    try {
      setLoading(true);
      setProcessingId(userId);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Make API call with auth header
      const response = await axios.put(
        `${API_ADMIN_END_POINT}/change-role/${userId}`, 
        { role: newRole }, // add role parameter
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(response.data?.message || `User role changed to ${newRole} successfully!`);
      
      // Refresh the users list to show updated roles
      await handleRefresh();
    } catch (error) {
      console.error('Error changing user role:', error);
      toast.error(error.response?.data?.message || 'Failed to change user role');
    } finally {
      setLoading(false);
      setProcessingId(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeletingId(userId);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Make API call with auth header
      const response = await axios.delete(
        `${API_ADMIN_END_POINT}/delete/user/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(response.data?.message || 'User deleted successfully');
      
      // Refresh the users list
      await handleRefresh();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold">User Management</h2>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, localUsers.length)} of {localUsers.length} users
            </div>
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center gap-2 px-3 py-2 rounded transition-colors ${
                refreshing 
                  ? 'bg-blue-100 text-blue-400 cursor-not-allowed' 
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
              title="Refresh user list"
            >
              <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
        {!hasAdmin && (
          <div className="p-4 bg-yellow-100 text-yellow-800 border-b">
            Warning: No admin users found. Please assign at least one user as admin.
          </div>
        )}
        {refreshing && (
          <div className="p-4 text-center text-gray-500">
            <svg className="animate-spin h-8 w-8 mx-auto mb-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p>Loading users...</p>
          </div>
        )}
        {!refreshing && (
          <>
            <div className="overflow-x-auto">
              <Table className="w-full">
                <Thead className="bg-gray-50">
                  <Tr>
                    <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</Th>
                    <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</Th>
                    <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</Th>
                    <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody className="divide-y divide-gray-200">
                  {currentUsers.map((user) => (
                    <Tr key={user._id}>
                      <Td className="px-6 py-4 whitespace-nowrap">{user.name}</Td>
                      <Td className="px-6 py-4 whitespace-nowrap">{user.email}</Td>
                      <Td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs ${user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {user.role}
                        </span>
                      </Td>
                      <Td className="px-6 py-4 whitespace-nowrap">
                        {user.role !== 'admin' && (
                          <button 
                            className="text-green-600 hover:text-green-800 mr-2"
                            onClick={() => handleChangeRole(user._id, 'admin')}
                            disabled={loading && processingId === user._id}
                            title="Make Admin"
                          >
                            {/* {loading && processingId === user._id ? (
                              <span className="inline-block w-5 h-5">
                                <svg className="animate-spin h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </span>
                            ) : (
                              <FiUserCheck className="w-5 h-5 inline" />
                            )} */}
                          </button>
                        )}
                        {user.role === 'admin' && adminCount > 1 && (
                          <button 
                            className="text-orange-600 hover:text-orange-800 mr-2"
                            onClick={() => handleChangeRole(user._id, 'user')}
                            disabled={loading && processingId === user._id}
                            title="Remove Admin"
                          >
                            {loading && processingId === user._id ? (
                              <span className="inline-block w-5 h-5">
                                <svg className="animate-spin h-5 w-5 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </span>
                            ) : (
                              <FiUserX className="w-5 h-5 inline" />
                            )}
                          </button>
                        )}
                        {/* <button className="text-blue-600 hover:text-blue-800 mr-2">
                          <FiEdit2 className="w-5 h-5 inline" />
                        </button> */}
                        <button 
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteUser(user._id)}
                          disabled={deletingId === user._id}
                          title="Delete User"
                        >
                          {deletingId === user._id ? (
                            <span className="inline-block w-5 h-5">
                              <svg className="animate-spin h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </span>
                          ) : (
                            <FiTrash2 className="w-5 h-5 inline" />
                          )}
                        </button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t">
              <div className="flex items-center gap-2 mb-4 sm:mb-0">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-full ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`w-8 h-8 rounded-full text-sm ${
                        currentPage === number
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                </div>
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-full ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
